import isApiKeyProvided from '@UI/Pages/Administration/AiEngines/Sidebars/ProviderSidebar/FormData/constraint/isApiKeyProvided';
import isApiVersionProvided from '@UI/Pages/Administration/AiEngines/Sidebars/ProviderSidebar/FormData/constraint/isApiVersionProvided';
import type AiProviderView from '@UI/Store/AiConfiguration/Views/AiProviderView';
import { AbstractValidatedClass, Joi, ValidatedClass, constraint } from '@verclam/form-validator';

export type AiProviderFormArgs = {
	label: string;
	kind: string;
	baseUrl: string;
	apiVersion: string;
	apiKey: string;
	isPlatformCredential: boolean;
	/** Drives `isApiKeyProvided`: never sent to the server, only read by the constraint. */
	isCreation: boolean;
};

export type AiProviderCommandData = {
	readonly label: string;
	readonly kind: string;
	readonly baseUrl: string;
	readonly apiVersion: string | null;
	readonly apiKey: string;
	readonly isPlatformCredential: boolean;
};

/**
 * `ignoreNullValue` is deliberately absent: with it, an empty/`null` value would be treated as valid
 * and `isApiKeyProvided`/`isApiVersionProvided` would never even run (`guides/forms.md`). The server
 * never re-serves the key, on any route: an edit form starts from a blank field whose absence on
 * submission means "keep the one already registered."
 */
@ValidatedClass()
class AiProviderFormData extends AbstractValidatedClass<AiProviderFormArgs> {
	@constraint(Joi.string().max(120), () => 'Le nom ne peut pas dépasser 120 caractères.')
	@constraint(Joi.string().required(), () => 'Le nom du fournisseur est obligatoire.')
	public readonly label: string;

	@constraint(Joi.string().required(), () => 'Choisissez un type de fournisseur.')
	public readonly kind: string;

	@constraint(
		Joi.string().uri({ scheme: 'https' }).max(500),
		() => 'L’URL de base doit être une adresse HTTPS valide.',
	)
	@constraint(Joi.string().required(), () => 'L’URL de base est obligatoire.')
	public readonly baseUrl: string;

	@constraint(Joi.string().max(40).allow(''), () => 'La version d’API ne peut pas dépasser 40 caractères.')
	@constraint(isApiVersionProvided, () => 'La version d’API est obligatoire pour Azure OpenAI.')
	public readonly apiVersion: string;

	@constraint(Joi.string().max(500).allow(''), () => 'La clé d’API ne peut pas dépasser 500 caractères.')
	@constraint(isApiKeyProvided, () => 'La clé d’API est obligatoire.')
	public readonly apiKey: string;

	public readonly isPlatformCredential: boolean;
	public readonly isCreation: boolean;

	constructor(data: AiProviderFormArgs) {
		super(data);
		this.label = data.label;
		this.kind = data.kind;
		this.baseUrl = data.baseUrl;
		this.apiVersion = data.apiVersion;
		this.apiKey = data.apiKey;
		this.isPlatformCredential = data.isPlatformCredential;
		this.isCreation = data.isCreation;
		Object.freeze(this);
	}

	static createEmpty(): AiProviderFormData {
		return new AiProviderFormData({
			label: '',
			kind: '',
			baseUrl: '',
			apiVersion: '',
			apiKey: '',
			isPlatformCredential: false,
			isCreation: true,
		});
	}

	/** The key is never prefilled: the server never serves it back, so an edit form starts from a
	 * blank field whose absence on submission means "keep the one already registered." */
	static fromView(provider: AiProviderView): AiProviderFormData {
		return new AiProviderFormData({
			label: provider.label,
			kind: provider.kind.value,
			baseUrl: provider.baseUrl,
			apiVersion: provider.apiVersion ?? '',
			apiKey: '',
			isPlatformCredential: provider.isPlatformCredential,
			isCreation: false,
		});
	}

	setLabel(label: string): this {
		return this.cloneWith({ label } as Partial<AiProviderFormArgs>);
	}

	setKind(kind: string): this {
		return this.cloneWith({ kind } as Partial<AiProviderFormArgs>);
	}

	setBaseUrl(baseUrl: string): this {
		return this.cloneWith({ baseUrl } as Partial<AiProviderFormArgs>);
	}

	setApiVersion(apiVersion: string): this {
		return this.cloneWith({ apiVersion } as Partial<AiProviderFormArgs>);
	}

	setApiKey(apiKey: string): this {
		return this.cloneWith({ apiKey } as Partial<AiProviderFormArgs>);
	}

	setIsPlatformCredential(isPlatformCredential: boolean): this {
		return this.cloneWith({ isPlatformCredential } as Partial<AiProviderFormArgs>);
	}

	/** Produces the fields shared by `CreateAiProvider` and `UpdateAiProvider`, so the sidebar never
	 * recopies them field by field. `apiVersion` collapses `''` to `null`, matching the server contract
	 * for a provider kind that carries no API version. */
	toCommandData(): AiProviderCommandData {
		return {
			label: this.label,
			kind: this.kind,
			baseUrl: this.baseUrl,
			apiVersion: this.apiVersion === '' ? null : this.apiVersion,
			apiKey: this.apiKey,
			isPlatformCredential: this.isPlatformCredential,
		};
	}
}

export default AiProviderFormData;
